import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useApi from '@/hooks/useApi';
import useSign from '@/pages/SignLayout.context';
import Button from '@/components/Button';
import FormError from '@/components/FormError';
import FormVerifyCode from '@/components/FormVerifyCode';
import HelpBlock from '@/components/HelpBlock';
import LoginGuardShell from './LoginGuardShell';
import styles from './Login.module.scss';

type SMSSudoLogin = {
  code: string;
};

const LoginSMS = () => {
  const { t } = useTranslation(['auth', 'common', 'sign']);
  const { workflowId } = useParams();
  const { setLoginSuccessful } = useSign();

  const [smsTriggered, setSmsTriggered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendAmount, setResendAmount] = useState<number>(0);
  const [resendLimit, setResendLimit] = useState<number>(23);
  const [inputCode, setInputCode] = useState<string>('');

  const [verifyAccess] = useApi('sign.verify');
  const [triggerSMS] = useApi('sign.sms.verify.start');

  const counterRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    setError,
    reset,
    formState: { errors },
  } = useForm<SMSSudoLogin>();

  const handleTriggerSMS = async () => {
    const result = await triggerSMS({ workflowId: workflowId ?? '' });
    setSmsTriggered(true);
    if (result?.response?.success) {
      setResendLimit(23);
    }
  };

  const resendLink = async () => {
    setLoading(true);
    reset({ code: '' });
    setInputCode('');
    setResendAmount((prevState) => {
      return prevState + 1;
    });

    const result = await triggerSMS({ workflowId: workflowId ?? '' });
    if (result?.response?.success) {
      setResendLimit(23);
    }
    setLoading(false);
  };

  const verifyPhone = async (code: string) => {
    setLoading(true);
    const res = await verifyAccess({
      smsCode: code,
      workflowId: workflowId ?? '',
    });
    const token = res?.response?.data.verificationToken;
    if (token) {
      sessionStorage.setItem('sudoToken', token);
      setLoginSuccessful();
    } else {
      setError('root', {
        message: t('auth:verifyPhone.errors.INVALID_CODE'),
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (resendLimit === 0) {
      return;
    }

    counterRef.current = setTimeout(() => {
      setResendLimit(resendLimit - 1);
    }, 1000);
  }, [resendLimit]);

  return (
    <LoginGuardShell
      introKey={
        smsTriggered ? 'sign:login.guard.smsIntro' : 'sign:login.guard.smsStart'
      }
    >
      {!smsTriggered && (
        <Button
          className={styles.fullButton}
          variant="primary"
          onClick={() => void handleTriggerSMS()}
        >
          {t('sign:verifyPhone.requestCode')}
        </Button>
      )}
      {smsTriggered && (
        <>
          {resendAmount > 3 && <HelpBlock content={t('sign:otpHelp')} />}
          <FormVerifyCode
            id="code"
            label={t('sign:verifyPhone.code')}
            ariaLabel={t('sign:verifyPhone.codePlaceholder')}
            onChange={(c: string) => setInputCode(c)}
            onComplete={verifyPhone}
            ref={inputRef}
            disabled={loading}
            value={inputCode}
          />
          {errors.root?.message && (
            <FormError isRoot message={errors.root.message} id="root-error" />
          )}
          <Button
            className={styles.fullButton}
            variant="primary"
            loading={loading}
            disabled={loading || inputCode.length < 6}
            onClick={() => void verifyPhone(inputCode)}
          >
            {t('sign:verifyPhone.verifyCode')}
          </Button>
          {resendLimit > 0 ? (
            <div className={styles.resendIn}>
              {t('sign:verifyPhone.resendCodeIn', { time: resendLimit })}
            </div>
          ) : (
            <Button
              className={styles.fullButton}
              variant="tertiary"
              loading={loading}
              disabled={loading}
              onClick={() => void resendLink()}
            >
              {t('sign:verifyPhone.resendCode')}
            </Button>
          )}
        </>
      )}
    </LoginGuardShell>
  );
};

export default LoginSMS;
